import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  Box,
  Stack,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { ThemeProvider } from "@mui/material/styles";
import muiTheme, { DIALOG_RADIUS, BUTTON_RADIUS } from "../../../theme/mui/styles";
import { fetchVendors } from "../../../api/vendors";

const toOption = (vendor) => ({
  value: vendor.id ?? vendor.value,
  label: vendor.name ?? vendor.label ?? "",
});

const dedupeOptions = (options = []) => {
  const map = new Map();
  options.forEach((opt) => {
    if (!opt || opt.value === undefined || opt.value === null) return;
    if (!map.has(opt.value)) {
      map.set(opt.value, opt);
    }
  });
  return Array.from(map.values());
};

const MergeVendorsModal = ({ isOpen, onClose, loading, handleSubmit }) => {
  const [originalVendor, setOriginalVendor] = useState(null);
  const [duplicateVendors, setDuplicateVendors] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [duplicateInput, setDuplicateInput] = useState("");
  const [originalInput, setOriginalInput] = useState("");

  const isFormValid = useMemo(() => {
    if (!originalVendor || duplicateVendors.length === 0) return false;
    return !duplicateVendors.some((v) => v.value === originalVendor);
  }, [originalVendor, duplicateVendors]);

  const loadVendors = useCallback(
    async (query = "") => {
      setSearching(true);
      try {
        const params = query.trim()
          ? { name__icontains: query.trim(), limit: 100 }
          : { limit: 100 };
        const data = await fetchVendors(params);
        const options =
          (data?.results || [])
            .map(toOption)
            .filter((opt) => opt.value !== undefined) || [];

        setVendorOptions((prev) => {
          const merged = dedupeOptions([
            ...prev,
            ...options,
            ...duplicateVendors,
          ]);

          // Ensure the selected original remains in options even if not in the latest fetch
          if (
            originalVendor &&
            !merged.some((opt) => opt.value === originalVendor)
          ) {
            const fallbackLabel =
              prev.find((opt) => opt.value === originalVendor)?.label ||
              options.find((opt) => opt.value === originalVendor)?.label ||
              "Selected vendor";
            merged.push({ value: originalVendor, label: fallbackLabel });
            return dedupeOptions(merged);
          }

          return merged;
        });
      } catch (err) {
        console.error("Failed to load vendors", err);
      } finally {
        setSearching(false);
      }
    },
    [duplicateVendors, originalVendor]
  );

  useEffect(() => {
    if (isOpen) {
      loadVendors();
    }
  }, [isOpen, loadVendors]);

  const handleOriginalChange = useCallback((_, option) => {
    setOriginalVendor(option?.value || null);
  }, []);

  const handleDuplicateSelect = useCallback(
    (_, option) => {
      if (!option?.value) return;
      if (duplicateVendors.find((v) => v.value === option.value)) return;
      setDuplicateVendors((prev) => [...prev, option]);
      setDuplicateInput("");
    },
    [duplicateVendors]
  );

  const removeDuplicateVendor = useCallback((vendor) => {
    setDuplicateVendors((prev) => prev.filter((v) => v.value !== vendor.value));
  }, []);

  const handleLocalSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!isFormValid) return;
      handleSubmit({
        original_vendor: originalVendor,
        duplicates: duplicateVendors.map((v) => v.value),
      });
      setOriginalVendor(null);
      setDuplicateVendors([]);
      setOriginalInput("");
      setDuplicateInput("");
    },
    [handleSubmit, originalVendor, duplicateVendors, isFormValid]
  );

  const filteredOptions = useMemo(
    () =>
      vendorOptions.filter(
        (opt) =>
          opt.value !== originalVendor &&
          !duplicateVendors.some((v) => v.value === opt.value)
      ),
    [vendorOptions, originalVendor, duplicateVendors]
  );

  return (
    <ThemeProvider theme={muiTheme}>
    <Dialog
      open={Boolean(isOpen)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
        PaperProps={{ sx: { borderRadius: DIALOG_RADIUS, mt: 10 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          pr: 5,
          fontWeight: 700,
          fontSize: 20,
        }}
      >
        Merge Vendors
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ ml: "auto" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5, pb: 1 }}>
        <form id="vendorMergeForm" onSubmit={handleLocalSubmit}>
          <Stack spacing={3}>
            <FormControl>
              <FormLabel>Original Vendor</FormLabel>
              <Autocomplete
                fullWidth
                size="small"
                options={vendorOptions}
                getOptionLabel={(option) => option.label || ""}
                value={
                  vendorOptions.find((opt) => opt.value === originalVendor) || null
                }
                inputValue={originalInput}
                onInputChange={(_, value) => {
                  setOriginalInput(value);
                  loadVendors(value);
                }}
                onChange={handleOriginalChange}
                loading={searching}
                componentsProps={{
                  popper: { sx: { zIndex: 1600 } },
                  paper: {
                    sx: {
                      bgcolor: "#536DEC",
                      color: "#fff",
                      "& .MuiAutocomplete-option": {
                        color: "#fff",
                        "&.Mui-focused": { bgcolor: "#536DEC" },
                        "&.Mui-selected": { bgcolor: "#536DEC" },
                      },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select vendor..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searching ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Duplicate Vendors</FormLabel>
              <Autocomplete
                fullWidth
                size="small"
                options={filteredOptions}
                getOptionLabel={(option) => option.label || ""}
                value={null}
                inputValue={duplicateInput}
                onInputChange={(_, value) => {
                  setDuplicateInput(value);
                  loadVendors(value);
                }}
                onChange={handleDuplicateSelect}
                loading={searching}
                componentsProps={{
                  popper: { sx: { zIndex: 1600 } },
                  paper: {
                    sx: {
                      bgcolor: "#536DEC",
                      color: "#fff",
                      "& .MuiAutocomplete-option": {
                        color: "#fff",
                        "&.Mui-focused": { bgcolor: "#536DEC" },
                        "&.Mui-selected": { bgcolor: "#536DEC" },
                      },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select vendor..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searching ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {duplicateVendors.length > 0 && (
                <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                  {duplicateVendors.map((v) => (
                    <Chip
                      key={v.value}
                      label={v.label}
                      onDelete={() => removeDuplicateVendor(v)}
                      deleteIcon={<DeleteIcon />}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </FormControl>

            <Box
              mt={2}
              border="1px solid #E5E7EB"
              borderRadius={1}
              maxHeight="calc(100vh - 480px)"
              overflow="auto"
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Duplicates to merge</TableCell>
                    <TableCell align="right" sx={{ width: 60 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {duplicateVendors.map((v) => (
                    <TableRow key={v.value} hover>
                      <TableCell>{v.label}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeDuplicateVendor(v)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {duplicateVendors.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography color="text.secondary" variant="body2">
                          No duplicates selected.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={onClose}
            sx={{ textTransform: "none", borderRadius: `${BUTTON_RADIUS}px !important` }}
            disabled={loading}
          >
          Cancel
        </Button>
        <Button
          type="submit"
          form="vendorMergeForm"
          variant="contained"
          disabled={!isFormValid || loading}
            sx={{ textTransform: "none", borderRadius: `${BUTTON_RADIUS}px !important` }}
        >
          {loading ? "Merging..." : "Merge"}
        </Button>
      </DialogActions>
    </Dialog>
    </ThemeProvider>
  );
};

export default MergeVendorsModal;
